import { useIntl } from 'react-intl';
import { Button, Form, Modal, Skeleton } from 'antd';

import { useHistory } from 'react-router-dom';
import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs, SaveButton } from '@/components/_common';

import PurchasesReleaseForm from '@/components/Movements/Purchases/PurchasesReleaseForm';
import { usePurchaseRemove } from '@/core/domain/purchase/Purchase.hooks';
import { useEffect } from 'react';
import { PurchaseCreateFormValues } from '@/core/domain/purchase/Purchase.types';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useReleasePurchaseFormContext } from '../../ReleasesPurchaseProvider';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import PurchasesButtonsRepeated from '@/components/Movements/Purchases/PurchasesButtonsRepeated';
import purchaseValidationTabs from '@/utils/functions/purchase/purchaseValidationTabs';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import useHasDiffValue from '@/hooks/hasDiffValue';
import PurchaseHeader from '@/components/Movements/Purchases/PurchaseHeader';

function PurchaseGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const {
    instanceForms,
    purchaseId,
    isLoading,
    disabled,
    purchase,
    loadingPurchase,
    purchaseReadOnly,
  } = useReleasePurchaseFormContext();

  const [form] = Form.useForm<PurchaseCreateFormValues>(
    instanceForms.releasePurchaseGeneral,
  );

  const formValues = Form.useWatch([], form);

  const hasDiffValue = useHasDiffValue(formValues);

  const removeMutation = usePurchaseRemove();

  useEffect(() => {
    if (purchase && !isEmpty(purchase)) {
      form.setFieldsValue({
        ...purchase,

        supplier: {
          id: purchase.supplierDTO.id,
          name: purchase.supplierDTO.name,
        },

        releaseDate: purchase.releaseDate
          ? moment(purchase.releaseDate)
          : undefined,

        issueDate: purchase.issueDate ? moment(purchase.issueDate) : undefined,

        departureDate: purchase.departureDate
          ? moment(purchase.departureDate)
          : undefined,

        deliveryPrevisionDate: purchase.deliveryPrevisionDate
          ? moment(purchase.deliveryPrevisionDate)
          : undefined,
      });
    }
  }, [purchase, instanceForms.releasePurchaseGeneral]);

  const handleSaveClick = async () => {
    try {
      await form.validateFields();

      form.submit();
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'purchases.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'purchases.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(Number(purchaseId));

        history.push('/app/movements/purchases/purchasesReleases');
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.movements.directSale.details.general',
        })}
      />
      <DefaultRouteTabs
        permissions={route.permissions}
        route={purchaseValidationTabs(route, purchase)}
        baseKey={purchaseId}
        basePath={`/app/movements/purchases/purchasesReleases/${purchaseId}`}
        extra={
          <>
            <SpaceForButton>
              <Button
                block
                danger
                onClick={handleRemoveClick}
                loading={removeMutation.isLoading}
                disabled={
                  loadingPurchase ||
                  purchase?.hasFinancial ||
                  purchaseReadOnly ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_DELETE,
                  ]) ||
                  purchase?.isDeletionDisabled
                }
              >
                {intl.formatMessage({ id: 'commons.entity.remove' })}
              </Button>

              <SaveButton
                block
                type="primary"
                onClick={handleSaveClick}
                loading={isLoading}
                disabled={
                  (!hasDiffValue && disabled) ||
                  purchaseReadOnly ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_EDIT,
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT,
                  ])
                }
              >
                {intl.formatMessage({ id: 'commons.entity.save' })}
              </SaveButton>
            </SpaceForButton>
          </>
        }
      >
        <Skeleton loading={loadingPurchase}>
          <PurchaseHeader />

          <PurchasesReleaseForm
            form={form}
            isEdit={!!purchaseId}
            purchase={purchase}
          />

          <PurchasesButtonsRepeated purchase={purchase} />
        </Skeleton>
      </DefaultRouteTabs>
    </>
  );
}

export default PurchaseGeneralInfoPage;
